/**
 * Slice 19d — unified create/edit form for library templates.
 * - Heading + primary button toggle between "Add new template" and "Edit template — {name}".
 * - In edit mode, an exemplars subsection (chip strip + indexed-ads picker + AI rerun)
 *   appears so the trainer can link saved posts to the template and re-derive the
 *   draft without leaving the panel.
 * - Save changes routes through editor.saveEditingTemplate (PATCH); Add template
 *   keeps the existing createAdsTemplate flow.
 */
import {
  createAdsTemplate,
  type AdsAssistantCategoryRow,
  type AdsAssistantPostTagDefRow,
  type AdsAssistantTemplateRow,
  type AdsLibraryAssistDraft,
} from "@/lib/adsAssistantClient";
import {
  AdsArchetypeTagChipStrip,
  AdsArchetypeTagMultiSelect,
} from "@/components/ads-assistant/AdsArchetypeTagMultiSelect";
import { AdsLibraryTemplateCustomExemplarsField } from "@/components/ads-assistant/AdsLibraryTemplateCustomExemplarsField";
import { AdsLibraryTemplateExemplarsSection } from "@/components/ads-assistant/AdsLibraryTemplateExemplarsSection";
import type { LibraryTemplateEditor } from "@/components/ads-assistant/useAdsAssistantLibraryTemplateEditor";

type Props = {
  canEdit: boolean;
  scopedOrgId: string | null;
  categories: AdsAssistantCategoryRow[];
  templates: AdsAssistantTemplateRow[];
  postTagDefs: AdsAssistantPostTagDefRow[];
  editor: LibraryTemplateEditor;
  setError: (e: string | null) => void;
  setBanner: (b: string | null) => void;
  load: () => Promise<void>;
};

export function AdsLibraryTemplateEditor(props: Props) {
  const { canEdit, scopedOrgId, categories, templates, postTagDefs, editor, setError, setBanner, load } = props;
  if (!canEdit) return null;

  const editingRow = editor.editingTemplateId
    ? templates.find((t) => t.id === editor.editingTemplateId) ?? null
    : null;

  function applyRerunDraft(d: AdsLibraryAssistDraft) {
    if (!d.template) return;
    editor.setTplName(d.template.name);
    editor.setTplBody(d.template.body_markdown);
    editor.setTplCategoryId(d.template.category_id ?? "");
    editor.setTplArchetypeTagIds(d.template.archetype_tag_ids ?? []);
    editor.setTplSuggestedTopicTagIds(d.template.suggested_topic_tag_ids ?? []);
  }

  async function onAddNew() {
    const slots = editor.parseSlotsOrError();
    if (slots === "error") return;
    const r = await createAdsTemplate({
      name: editor.tplName.trim(),
      body_markdown: editor.tplBody,
      category_id: editor.tplCategoryId || null,
      ...(slots.length ? { slots_schema: slots } : {}),
      ...(editor.linkTplExamples && editor.tplExamplePostIds.length
        ? { example_social_post_ids: editor.tplExamplePostIds }
        : {}),
      ...(editor.tplCustomExemplarTexts.length
        ? { custom_exemplar_texts: editor.tplCustomExemplarTexts }
        : {}),
      ...(editor.tplArchetypeTagIds.length ? { archetype_tag_ids: editor.tplArchetypeTagIds } : {}),
    });
    if (!r.ok) {
      setError(r.error);
      return;
    }
    editor.resetEditor();
    setBanner("Template added.");
    void load();
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-zinc-900">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
          {editor.isEditing ? `Edit template${editingRow ? ` — ${editingRow.name}` : ""}` : "Add new template"}
        </h4>
        {editor.isEditing ? (
          <button
            type="button"
            onClick={() => editor.resetEditor()}
            className="text-[11px] text-gray-700 hover:underline dark:text-gray-300"
          >
            Cancel
          </button>
        ) : null}
      </div>

      <div className="mt-2 space-y-2">
        <input
          className="w-full rounded border border-gray-200 px-2 py-1 text-xs"
          placeholder="Template name"
          value={editor.tplName}
          onChange={(e) => editor.setTplName(e.target.value)}
        />
        <select
          className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs"
          value={editor.tplCategoryId}
          onChange={(e) => editor.setTplCategoryId(e.target.value)}
        >
          <option value="">Category (optional)</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
        <textarea
          className="w-full rounded border border-gray-200 px-2 py-1 text-xs font-mono min-h-[88px]"
          placeholder="Body / copy shell (markdown)"
          value={editor.tplBody}
          onChange={(e) => editor.setTplBody(e.target.value)}
        />
        <textarea
          className="w-full rounded border border-gray-200 px-2 py-1 text-xs font-mono min-h-[52px]"
          placeholder='Slots JSON array e.g. [{"id":"hook","label":"Hook"},{"id":"cta","label":"CTA"}]'
          value={editor.tplSlotsJson}
          onChange={(e) => editor.setTplSlotsJson(e.target.value)}
        />
        <AdsArchetypeTagMultiSelect
          tagDefs={postTagDefs}
          value={editor.tplArchetypeTagIds}
          onChange={editor.setTplArchetypeTagIds}
          idPrefix="ads-tpl-archetype"
        />
        {editor.tplSuggestedTopicTagIds.length ? (
          <div className="rounded border border-emerald-200 bg-emerald-50/60 p-2 text-[11px] text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/20 dark:text-emerald-100">
            <span className="font-semibold">AI-suggested topic tags (preview only):</span>
            <AdsArchetypeTagChipStrip tagDefs={postTagDefs} value={editor.tplSuggestedTopicTagIds} />
          </div>
        ) : null}

        <AdsLibraryTemplateCustomExemplarsField
          texts={editor.tplCustomExemplarTexts}
          setTexts={editor.setTplCustomExemplarTexts}
        />

        {editor.isEditing ? (
          <AdsLibraryTemplateExemplarsSection
            scopedOrgId={scopedOrgId}
            selectedIds={editor.tplExamplePostIds}
            setSelectedIds={editor.setTplExamplePostIds}
            customExemplarTexts={editor.tplCustomExemplarTexts}
            setError={setError}
            setBanner={setBanner}
            applyRerunDraft={applyRerunDraft}
          />
        ) : editor.tplExamplePostIds.length ? (
          <label className="flex items-start gap-2 text-[11px] text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={editor.linkTplExamples}
              onChange={(e) => editor.setLinkTplExamples(e.target.checked)}
            />
            <span>
              Save <strong className="font-medium">{editor.tplExamplePostIds.length}</strong> selected indexed post(s) as
              curated examples on this template (sets retention-safe guard; does <strong className="font-medium">not</strong>{" "}
              change each post&apos;s template badge).
            </span>
          </label>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {editor.isEditing ? (
            <button
              type="button"
              disabled={editor.saving || !editor.tplName.trim()}
              className="flex-1 rounded-lg bg-blue-700 px-2 py-1.5 text-xs font-medium text-white hover:bg-blue-800 disabled:opacity-50"
              onClick={() => void editor.saveEditingTemplate()}
            >
              {editor.saving ? "Saving…" : "Save changes"}
            </button>
          ) : (
            <button
              type="button"
              disabled={!editor.tplName.trim()}
              className="flex-1 rounded-lg bg-blue-700 px-2 py-1.5 text-xs font-medium text-white hover:bg-blue-800 disabled:opacity-50"
              onClick={() => void onAddNew()}
            >
              Add template
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
